import React, { useEffect, useRef, useState } from 'react'
import { useExclusiveMenu } from '../../hooks/useExclusiveMenu'
import FormControl from '@mui/material/FormControl'
import { useAppDispatch } from '../../redux/store'
import { getMissionbyId } from '../../redux/missions/missionActions'
import { Mission } from '../../types/missionTypes'
import Select, { SingleValue } from 'react-select'
import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { incyiaSelectStyles } from '../../utils/incyiaSelectStyles'
import { getAllMissions } from '../../api/missionsApi'
import { useAsyncOptions } from '../../hooks/useAsyncOptions'
import { createOptionProps } from './CreateOptionMenu'

interface SelectMissionProps {
  value: string
  onChange: (newMission: string) => void
  /** Adds "+ New mission" to the menu; receives the typed search text. */
  onCreateNew?: (search: string) => void
  /** Bump to refetch the options (e.g. after a quick create). */
  reloadKey?: number
  /** Keep the current mission, in addition to the add-task route lock. */
  disabled?: boolean
}

const SelectMission: React.FC<SelectMissionProps> = ({
  value,
  onChange,
  onCreateNew,
  reloadKey = 0,
  disabled = false,
}) => {
  const menu = useExclusiveMenu()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const { idmission } = useParams<{ idmission: string; id: string }>()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [mission, setMission] = useState<{
    value: string
    label: string
  } | null>(null)

  const { data: missions, status, retry } = useAsyncOptions(async () => {
    const response = await getAllMissions({ search: '', page: 1, pageSize: 200 })
    return response.missions ?? []
  }, [reloadKey])


  useEffect(() => {
    if (value) {
      dispatch(getMissionbyId(value))
        .unwrap()
        .then((mission: Mission) => {
          setMission({
            value: mission._id as string,
            label: mission.MissionName,
          })
        })
        .catch((error) => console.error('Error fetching mission by ID:', error))
    } else {
      setMission(null)
    }
  }, [value, dispatch])
  const missionOptions =
    status === 'ready'
      ? (missions ?? []).map((item: Mission) => ({
          value: item._id || '',
          label: item.MissionName || '',
        }))
      : []

  const handleMissionChange = (
    selected: SingleValue<{ value: string; label: string }>
  ) => {
    if (selected) {
      onChange(selected.value)
    } else {
      onChange('')
    }
  }
  return (
    <div ref={containerRef} style={{ width: '100%', position: 'relative' }}>
      <FormControl fullWidth style={{ width: '100%' }}>
        <Select
          options={missionOptions}
          value={mission}
          onChange={handleMissionChange}
          isLoading={status === 'loading'}
          isDisabled={disabled || !!idmission || status === 'loading'}
          placeholder={
            status === 'loading'
              ? t('selectOptions.loading')
              : t('addPipelinePage.selectMission')
          }
          noOptionsMessage={() =>
            status === 'error' ? t('selectOptions.failed') : t('tables.noData')
          }
          {...menu}
          styles={incyiaSelectStyles}
          {...createOptionProps(
            onCreateNew && {
              label: t('quickCreate.new.mission'),
              onCreate: onCreateNew,
            }
          )}
        />
      </FormControl>
      {status === 'error' ? (
        <button
          type="button"
          onClick={retry}
          style={{
            marginTop: 6,
            background: 'none',
            border: 'none',
            padding: 0,
            color: '#076B5C',
            cursor: 'pointer',
            fontFamily: 'Lato, sans-serif',
            fontSize: 13,
          }}
        >
          {t('selectOptions.retry')}
        </button>
      ) : null}
    </div>
  )
}

export default SelectMission
